import { lazy, Suspense, useState } from "react";
import { Presentation } from "lucide-react";
import type { MentionQuery } from "@/shared/api/api-client";
import type { ReportRecorte } from "@/features/reports/report-recorte";
import { versaEnabled } from "../config";

// A janela (e o que ela puxa: lista de relatórios e modelos do Versa) só
// carrega no clique — a tela de Menções não paga pelo Versa sem usá-lo.
const SendToVersaDialog = lazy(() => import("./SendToVersaDialog").then(module => ({ default: module.SendToVersaDialog })));

/** O recorte do clique, nas duas línguas: a do relatório do Reputaition
 * (gerar um relatório novo) e a da lista de Menções (juntar a um
 * rascunho). */
export type VersaSendRecorte = {
  report: ReportRecorte;
  mentions: MentionQuery;
  /** Filtros da tela que NÃO entram num relatório — os mesmos do aviso
   * do "Fechar amostra". */
  notInReport: string[];
};

/**
 * "Enviar para o Versa" na tela de Menções (ponto de encaixe — ver
 * `versa/ARCHITECTURE.md`). Desde 2026-10-19 gera um RELATÓRIO do
 * Reputaition com o filtro da tela (o CENA de rede só existe calculado no
 * relatório) e liga o relatório Versa a ele — ou junta o filtro a uma base
 * rascunho. Some com o Versa desligado.
 */
export function SendToVersaButton({
  token,
  buildQuery,
  buildRecorte,
  notInReport,
  disabledReason = null,
}: {
  token: string;
  buildQuery: () => MentionQuery;
  buildRecorte: () => ReportRecorte;
  notInReport: () => string[];
  /** O mesmo motivo que trava o "Fechar amostra" (ex.: só menções sem
   * classificação), ou `null`. */
  disabledReason?: string | null;
}) {
  const [recorte, setRecorte] = useState<VersaSendRecorte | null>(null);
  if (!versaEnabled) return null;
  return (
    <>
      <button
        type="button"
        className="secondary-button"
        disabled={disabledReason !== null}
        title={disabledReason ?? undefined}
        onClick={() => setRecorte({ report: buildRecorte(), mentions: buildQuery(), notInReport: notInReport() })}
      >
        <Presentation size={16} />
        Enviar para o Versa
      </button>
      {recorte && (
        <Suspense fallback={null}>
          <SendToVersaDialog token={token} recorte={recorte} onClose={() => setRecorte(null)} />
        </Suspense>
      )}
    </>
  );
}
